import React, { useEffect, useMemo, useState } from 'react'
import { Dropdown, Form } from 'react-bootstrap'
import { TablePagination } from './TablePagination'
import dayjs from 'dayjs'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import ModalImagen from './Modal/ModalImagen'
import utc from 'dayjs/plugin/utc'
import { obtenerDescripcionPorId, truncateText } from './helpers/helpers'
import { useThemeMode } from '../../../../_metronic/partials'

dayjs.extend(utc)
export type ColumnType = 'string' | 'number' | 'date' | 'boolean' | 'time'

export interface Column<T> {
  key: keyof T | string
  label: string
  type: ColumnType
  render?: (row: T) => React.ReactNode
  showTime?: boolean
  className?: string
  id?: boolean
  show?: boolean
  url?: (row: T) => string
  utc?: boolean
  url_consentimiento?: boolean
  localizacion?: boolean
  carImage?: (row: T) => string
}

interface AdvancedTableProps<T> {
  data: T[]
  columns: Column<T>[]
  loading?: boolean
  error?: string
  currentPage?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  onView?: (row: T) => void
  onDelete?: (row: T) => void
  onSelect?: (row: T) => void
  selectionKey?: keyof T
  secondarySelectionKey?: keyof T

  showActions?: boolean
  visibleColumnKeys?: (keyof T)[]
  rowsPerPage?: number
  onRowsPerPageChange?: (rows: number) => void
  rowsPerPageOptions?: number[]
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj)
}

export function AdvancedTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  error,
  currentPage,
  totalPages,
  onPageChange,
  onView,
  onDelete,
  onSelect,
  selectionKey,
  secondarySelectionKey,

  showActions = false,
  visibleColumnKeys,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 15, 20],
}: AdvancedTableProps<T>) {
  const [sortConfig, setSortConfig] = useState<{ key: keyof T; direction: 'asc' | 'desc' } | null>(
    null
  )

  const [filters, setFilters] = useState<Record<string, string>>({})
  const [openDropdown, setOpenDropdown] = useState<keyof T | null>(null)
  const { mode } = useThemeMode()

  const handleDropdownToggle = (key: keyof T) => {
    setOpenDropdown((prevKey) => (prevKey === key ? null : key))
  }
  const closeDropdown = () => setOpenDropdown(null)

  const displayedColumns = useMemo(() => {
    return visibleColumnKeys?.length
      ? columns.filter((col) => visibleColumnKeys.includes(col.key))
      : columns
  }, [columns, visibleColumnKeys])

  const [filteredCount, setFilteredCount] = useState(0)

  const filteredSortedData = useMemo(() => {
    let filtered = data.filter((item) =>
      displayedColumns.every((col) => {
        const val = getNestedValue(item, col.key as string)
        const filter = filters[col.key as string] ?? ''
        if (filter === '') return true

        switch (col.type) {
          case 'string':
            return String(val ?? '')
              .toLowerCase()
              .includes(filter.toLowerCase())
          case 'number':
            return val?.toString().includes(filter)
          case 'date':
            const [year, month, day] = filter.split('-')
            const localDate = new Date(Number(year), Number(month) - 1, Number(day))
            const cellDate = new Date(val).toLocaleDateString()
            const filterDate1 = localDate.toLocaleDateString()
            return cellDate === filterDate1
          case 'boolean':
            return filter === 'true' ? val === true : filter === 'false' ? val === false : true

          case 'time':
          case 'time':
            if (!val || filter === '') return true

            const horaCelda = dayjs(val).format('HH:mm')

            const filtroNormalizado = filter.trim()

            return horaCelda.startsWith(filtroNormalizado)

          default:
            return true
        }
      })
    )

    setFilteredCount(filtered.length)

    if (sortConfig) {
      const { key, direction } = sortConfig
      const colType = columns.find((col) => col.key === key)?.type

      filtered = [...filtered].sort((a, b) => {
        let aVal = getNestedValue(a, key as string)
        let bVal = getNestedValue(b, key as string)

        const aIsEmpty = aVal === null || aVal === undefined || aVal === ''
        const bIsEmpty = bVal === null || bVal === undefined || bVal === ''

        if (aIsEmpty && !bIsEmpty) return direction === 'asc' ? 1 : -1
        if (!aIsEmpty && bIsEmpty) return direction === 'asc' ? -1 : 1
        if (aIsEmpty && bIsEmpty) return 0

        if (colType === 'date') {
          const aDate = new Date(aVal)
          const bDate = new Date(bVal)
          return direction === 'asc'
            ? aDate.getTime() - bDate.getTime()
            : bDate.getTime() - aDate.getTime()
        }

        if (aVal < bVal) return direction === 'asc' ? -1 : 1
        if (aVal > bVal) return direction === 'asc' ? 1 : -1
        return 0
      })
    }

    if (rowsPerPage && currentPage != null) {
      const start = (currentPage - 1) * rowsPerPage
      const end = start + rowsPerPage
      return filtered.slice(start, end)
    }

    return filtered
  }, [data, displayedColumns, filters, sortConfig, rowsPerPage, currentPage, filteredCount])

  const handleSort = (key: keyof T, direction: 'asc' | 'desc') => {
    setSortConfig({ key, direction })
  }

  const clearSort = (key: keyof T) => {
    if (sortConfig?.key === key) {
      setSortConfig(null)
    }
  }

  const handleFilterChange = (key: keyof T, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    onPageChange?.(1)
  }

  const clearFilter = (key: keyof T) => {
    setFilters((prev) => ({ ...prev, [key]: '' }))
    onPageChange?.(1)
  }

  const isColumnActive = (key: keyof T | string) => {
    const filterValue = filters[key as string]
    const hasFilter = typeof filterValue === 'string' && filterValue.trim() !== ''
    const isSorted = sortConfig?.key === key
    return hasFilter || isSorted
  }

  const [filaSeleccionada, setFilaSeleccionada] = useState<string | number | null>(null)
  const [idSeleccionado, setIdSeleccionado] = useState<string | number | null>(null)
  const [valorSecundario, setValorSecundario] = useState<string | number | null>(null)

  const manejarSeleccionFila = (row: T) => {
    if (!selectionKey) return

    const id = getNestedValue(row, selectionKey as string)
    const secundario = secondarySelectionKey
      ? getNestedValue(row, secondarySelectionKey as string)
      : null

    if (filaSeleccionada === id) {
      setFilaSeleccionada(null)
      setIdSeleccionado(null)
      setValorSecundario(null)
    } else {
      setFilaSeleccionada(id)
      setIdSeleccionado(id)
      setValorSecundario(secundario)
    }

    onSelect?.(row)
  }

  const [showModalImagen, setShowModalImagen] = useState(false)
  const [urlImagen, setUrlImagen] = useState('')
  const [nameImagen, setNameImagen] = useState('')

  const handleShowModalImagen = (imagen: string, title: string) => {
    setUrlImagen(imagen)
    setNameImagen(title)
    setShowModalImagen(true)
  }

  const handleCloseModalImagen = () => {
    setShowModalImagen(false)
  }
  return (
    // <div className='card-body d-flex flex-column justify-content-between' style={{ height: 'calc(100vh - 320px)' }} >
    //   <div>
    //     <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
    //       {error && <div className='alert alert-danger'>{error}</div>}

    //       <table className='table align-middle table-hover'>
    //         <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>

    <div className='card-body'>
      <div>
        <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 400px)', overflowX: 'auto', position: 'relative' }}>
          {error && <div className='alert alert-danger'>{error}</div>}

          <table className='table align-middle table-hover'>
            <thead>
              <tr className='fw-bold text-muted bg-light '>
                {displayedColumns.map((col) => (
                  <th
                    key={col.key as string}
                    className={`position-relative  ${col.className ?? 'min-w-150p'}`}
                  >
                    <div className='d-flex justify-content-between align-items-center'>
                      <span>{col.label}</span>

                      <div className='dropdown'>
                        <button
                          className='btn btn-sm btn-icon btn-light'
                          type='button'
                          data-bs-toggle='dropdown'
                        >
                          {sortConfig?.key === col.key && (
                            <span className='text-dark'>
                              {sortConfig.direction === 'asc' ? '▲' : '▼'}
                            </span>
                          )}

                          {filters[col.key as string] && (
                            <i className='bi bi-funnel-fill text-dark ms-1'></i>
                          )}

                          {!filters[col.key as string] && sortConfig?.key !== col.key && (
                            <i className='bi bi-three-dots-vertical'></i>
                          )}
                        </button>

                        <ul className='dropdown-menu p-2' style={{ minWidth: 180 }}>
                          <li className='mb-2'>
                            {'options' in col && Array.isArray((col as any).options) ? (
                              <select
                                className='form-select form-select-sm'
                                value={filters[col.key as string] || ''}
                                onChange={(e) => handleFilterChange(col.key, e.target.value)}
                              >
                                <option value=''>-- Todos --</option>
                                {(col as any).options.map((opt: any) => (
                                  <option key={String(opt.value)} value={String(opt.value)}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            ) : col.type === 'boolean' ? (
                              <select
                                className='form-select form-select-sm'
                                value={filters[col.key as string] || ''}
                                onChange={(e) => handleFilterChange(col.key, e.target.value)}
                              >
                                <option value=''>-- Todos --</option>
                                <option value='true'>Sí</option>
                                <option value='false'>No</option>
                              </select>
                            ) : (
                              <input
                                type={
                                  col.type === 'date'
                                    ? 'date'
                                    : col.type === 'number'
                                      ? 'number'
                                      : col.type === 'time'
                                        ? 'time'
                                        : 'text'
                                }
                                className='form-control form-control-sm'
                                placeholder='Buscar...'
                                value={filters[col.key as string] || ''}
                                onChange={(e) => handleFilterChange(col.key, e.target.value)}
                              />
                            )}
                          </li>

                          <li>
                            <button
                              className='dropdown-item'
                              onClick={() => {
                                const direction =
                                  sortConfig?.key === col.key && sortConfig.direction === 'asc'
                                    ? 'desc'
                                    : 'asc'
                                handleSort(col.key, direction)
                              }}
                            >
                              Ordenar
                            </button>
                          </li>

                          <li>
                            <button
                              className='dropdown-item text-danger'
                              onClick={() => {
                                clearFilter(col.key)
                                clearSort(col.key)
                              }}
                            >
                              Limpiar
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </th>
                ))}
                {showActions && <th className='min-w-150px text-center pb-5 bg-secondary text-primary' style={{ position: "sticky", right: "0", zIndex: "5" }}>Acciones</th>}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className='text-center'>
                    <div className='d-flex justify-content-center '>
                      <span
                        className='indicator-progress text-gray-500 fw-bold fs-3'
                        style={{ display: 'block' }}
                      >
                        Cargando datos
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    </div>
                  </td>
                </tr>
              ) : filteredSortedData.length === 0 ? (
                <tr>
                  <td colSpan={12} className='text-center'>
                    <h1 className='text-center text-secondary'>
                      Información no existe para esta búsqueda
                    </h1>
                  </td>
                </tr>
              ) : (
                filteredSortedData.map((item, idx) => (
                  <tr
                    key={idx}
                    onClick={() => {
                      manejarSeleccionFila(item)
                    }}
                    className={`cursor-pointer ${selectionKey &&
                      filaSeleccionada === getNestedValue(item, selectionKey as string)
                      ? 'bg-secondary rounded-start rounded-end'
                      : ''
                      }`}
                  >
                    {displayedColumns.map((col) => {
                      const val = getNestedValue(item, col.key as string)
                      let displayVal: React.ReactNode

                      // if (col.render) {
                      //   displayVal = col.render(item)
                      // } else {
                      //   displayVal =
                      //     col.type === 'date'
                      //       ? val
                      //         ? (col.utc ? dayjs.utc(val) : dayjs(val)).format(
                      //             col.showTime ? 'DD/MM/YYYY hh:mm A' : 'DD/MM/YYYY'
                      //           )
                      //         : ''
                      //       : col.type === 'boolean'
                      //       ? val
                      //         ? 'Sí'
                      //         : 'No'
                      //       : val ?? ''
                      // }

                      if (col.render) {
                        displayVal = col.render(item)
                      } else {
                        if (col.type === 'date') {
                          displayVal = val
                            ? (col.utc ? dayjs.utc(val) : dayjs(val)).format(
                              col.showTime ? 'DD/MM/YYYY hh:mm A' : 'DD/MM/YYYY'
                            )
                            : ''
                        } else if (col.type === 'time') {
                          displayVal = val
                            ? (col.utc ? dayjs.utc(val) : dayjs(val)).format('hh:mm A')
                            : ''
                        } else if (col.type === 'boolean') {
                          displayVal = val ? 'Sí' : 'No'
                        } else {
                          displayVal = val ?? ''
                        }
                      }

                      if (col.localizacion && typeof displayVal === 'number') {
                        displayVal = obtenerDescripcionPorId(displayVal)
                      }

                      // if (col.key === 'ResourceDetailsFullName') {
                      //   const imageUrl =
                      //     col.carImage?.(item) ||
                      //     (mode === 'dark'
                      //       ? toAbsoluteUrl('/media/svg/files/blank-image-dark.svg')
                      //       : toAbsoluteUrl('/media/svg/files/blank-image.svg'))

                      //   displayVal = (
                      //     <div className='d-flex align-items-center'>
                      //       <div className='me-4' style={{width: '200px', height: '120px'}}>
                      //         <div className='w-100 h-100 overflow-hidden rounded shadow-sm'>
                      //           <img
                      //             src={imageUrl}
                      //             alt='Imagen del recurso'
                      //             className='img-fluid w-100 h-100 object-fit-cover'
                      //           />
                      //         </div>
                      //       </div>
                      //       <div>
                      //         <span className='text-dark fw-semibold fs-6'>{val}</span>
                      //       </div>
                      //     </div>
                      //   )
                      // }

                      if (col.carImage) {
                        const imageUrl =
                          col.carImage?.(item) ||
                          (mode === 'dark'
                            ? toAbsoluteUrl('/media/svg/files/blank-image-dark.svg')
                            : toAbsoluteUrl('/media/svg/files/blank-image.svg'))

                        displayVal = (
                          <div className='d-flex align-items-center'>
                            <div className='me-4' style={{ width: '200px', height: '120px' }}>
                              <div className='w-100 h-100 overflow-hidden rounded shadow-sm'>
                                <img
                                  src={imageUrl}
                                  alt='Imagen del recurso'
                                  className='img-fluid w-100 h-100 object-fit-cover'
                                />
                              </div>
                            </div>
                            <div>
                              {/* 👇 Aquí usamos displayVal (ya procesado con render) */}
                              <span className='text-dark fw-semibold fs-6'>{displayVal || ''}</span>
                            </div>
                          </div>
                        )
                      }

                      const isConsentUrl = col.url_consentimiento && typeof val === 'string'

                      return (
                        <td
                          className={`${col.id ? 'ps-4' : ''} ${col.url ? 'cursor-pointer text-hover-primary' : ''
                            } ${col.type === 'boolean' ? 'ps-8' : ''}`}
                          key={`${col.key as string}-${idx}`}
                          onClick={(e) => {
                            if (col.url) {
                              e.stopPropagation()
                              const url = col.url(item)
                              const title = typeof displayVal === 'string' ? displayVal : ''
                              handleShowModalImagen(url, title)
                            }
                          }}
                        >
                          {isConsentUrl ? (
                            <a
                              href={val}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-dark text-hover-primary mb-1 fs-6'
                            >
                              {truncateText(val, 35)}
                            </a>
                          ) : col.url && typeof displayVal === 'string' ? (
                            displayVal.includes('_') ? (
                              displayVal.substring(displayVal.indexOf('_') + 1)
                            ) : (
                              displayVal
                            )
                          ) : (
                            displayVal
                          )}
                        </td>
                      )
                    })}

                    {showActions && (
                      <td className='text-center bg-body' style={{ position: "sticky", right: "0", zIndex: "5", boxShadow: "-2px 0 4px rgba(0,0,0,0.1)", filter: "drop-shadow(-2px 0 4px rgba(0,0,0,0.1))" }}>
                        {onView && (
                          <a
                            onClick={() => onView(item)}
                            className='btn btn-icon btn-bg-light btn-active-color-warning btn-sm me-1'
                          >
                            <i className='fas fa-eye svg-icon-3'></i>
                          </a>
                        )}
                        {onDelete && (
                          <a
                            onClick={() => onDelete(item)}
                            className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                          >
                            <KTSVG
                              path='/media/icons/duotune/general/gen027.svg'
                              className='svg-icon-3'
                            />
                          </a>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ModalImagen
        showModal={showModalImagen}
        handleCloseModal={handleCloseModalImagen}
        urlDoc={urlImagen}
        title={nameImagen}
      />

      <div>
        {onPageChange && totalPages && currentPage != null && (
          <TablePagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredCount / (rowsPerPage || 1))}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={rowsPerPageOptions}
            totalRecords={filteredCount}
          />
        )}
      </div>
    </div>
  )
}
